import { CONCEPT_VALUE_ALL_OPTION } from "../constants";
import type { OtoPlan } from "../../../services/objectToObjectPlanService";
import {
  ReferenceItemsRnCell,
  type ReferenceItemDetail,
} from "../TaggingPlan/ReferenceItemsCell";

const REFERENCE_DOC_TYPE_DISPLAY = "DocType";

/**
 * Build OTO homepage Reference Items cards (R1, R2, …) from plan
 * `reference_items` (or legacy object subtype).
 */
export function buildOtoReferenceItemDetails(
  plan: OtoPlan,
): ReferenceItemDetail[] {
  const objectName =
    plan.objectATypeName?.trim() || plan.objectAType.trim() || "";

  if (plan.referenceItems.length > 0) {
    if (!objectName) return [];
    return plan.referenceItems.map((item, index) => ({
      label: `R${index + 1}`,
      object: objectName,
      dimension: item.display_name?.trim() || item.concept.trim() || "",
      value: item.all_values
        ? CONCEPT_VALUE_ALL_OPTION.name
        : item.values
            .map((value) => value.name.trim() || value.code)
            .filter(Boolean)
            .join(", "),
    }));
  }

  if (!objectName) return [];

  const subTypeName = plan.objectASubTypeName?.trim() || "";
  if (!subTypeName) {
    return [
      {
        label: "R1",
        object: objectName,
        dimension: "",
        value: "",
      },
    ];
  }

  const nameParts = subTypeName
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  const codeParts = (plan.objectASubType || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  const isAll =
    (codeParts.length === 1 &&
      codeParts[0]!.toLowerCase() ===
        CONCEPT_VALUE_ALL_OPTION.code.toLowerCase()) ||
    nameParts.some(
      (name) =>
        name.toLowerCase() === CONCEPT_VALUE_ALL_OPTION.name.toLowerCase(),
    );

  return [
    {
      label: "R1",
      object: objectName,
      dimension: REFERENCE_DOC_TYPE_DISPLAY,
      value: isAll ? CONCEPT_VALUE_ALL_OPTION.name : nameParts.join(", "),
    },
  ];
}

/**
 * Build OTO homepage Impact Items cards (I1, I2, …) from plan
 * `impacted_items` (or legacy object B subtype).
 */
export function buildOtoImpactItemDetails(
  plan: OtoPlan,
): ReferenceItemDetail[] {
  const objectName =
    plan.objectBTypeName?.trim() || plan.objectBType.trim() || "";

  if (plan.impactedItems.length > 0) {
    if (!objectName) return [];
    return plan.impactedItems.map((item, index) => ({
      label: `I${index + 1}`,
      object: objectName,
      dimension: item.display_name?.trim() || item.concept.trim() || "",
      value: item.all_values
        ? CONCEPT_VALUE_ALL_OPTION.name
        : item.values
            .map((value) => value.name.trim() || value.code)
            .filter(Boolean)
            .join(", "),
    }));
  }

  if (!objectName) return [];

  const subTypeName = plan.objectBSubTypeName?.trim() || "";
  if (!subTypeName) {
    return [
      {
        label: "I1",
        object: objectName,
        dimension: "",
        value: "",
      },
    ];
  }

  const nameParts = subTypeName
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  const codeParts = (plan.objectBSubType || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  const isAll =
    (codeParts.length === 1 &&
      codeParts[0]!.toLowerCase() ===
        CONCEPT_VALUE_ALL_OPTION.code.toLowerCase()) ||
    nameParts.some(
      (name) =>
        name.toLowerCase() === CONCEPT_VALUE_ALL_OPTION.name.toLowerCase(),
    );

  return [
    {
      label: "I1",
      object: objectName,
      dimension: REFERENCE_DOC_TYPE_DISPLAY,
      value: isAll ? CONCEPT_VALUE_ALL_OPTION.name : nameParts.join(", "),
    },
  ];
}

type OtoReferenceItemsCellProps = Readonly<{
  plan: OtoPlan;
}>;

/** OTO table Reference Items — same R1/R2 dashed + hover style as Classification. */
export default function OtoReferenceItemsCell({
  plan,
}: OtoReferenceItemsCellProps) {
  return <ReferenceItemsRnCell details={buildOtoReferenceItemDetails(plan)} />;
}

/** OTO table Impact Items — same dashed + hover style, labeled I1, I2, …. */
export function OtoImpactItemsCell({ plan }: OtoReferenceItemsCellProps) {
  return (
    <ReferenceItemsRnCell
      details={buildOtoImpactItemDetails(plan)}
      itemKindLabel="Impact"
    />
  );
}
